import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import MailIcon from "@mui/icons-material/Mail";
import DeleteIcon from "@mui/icons-material/Delete";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import {
  db,
  collection,
  getDocs,
  doc,
  updateDoc,
} from "../../../../firebaseConfig";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";

import { useAtom } from "jotai";
import {
  themeAtom,
  currentStaffDataAtom,
  outsourcerEmailOpenAtom,
  outsourcerEmailDataAtom,
} from "../../../../-Atoms";
import UniversalTextField from "../../../../GlobalComponents/UniversalTextField";
import OutsourcerBriefsContainer from "./OutsourcerBriefsContainer";
import OutsourcerEmailV2 from "../../../../EmailModalV2/OutsourcerEmailV2";


const OutsourcersTab = ({ jobData, clientData, setHasUnsavedChanges }) => {
  const [theme] = useAtom(themeAtom);
  const [currentStaffData] = useAtom(currentStaffDataAtom);
  
  // Clean outsourcer email atoms
  const [outsourcerEmailOpen, setOutsourcerEmailOpen] = useAtom(outsourcerEmailOpenAtom);
  const [outsourcerEmailData, setOutsourcerEmailData] = useAtom(outsourcerEmailDataAtom);

  const [deleteJobDialog, setDeleteJobDialog] = useState({ open: false, jobIndex: null });
  const [outsourcerList, setOutsourcerList] = useState([]);
  const [outsourcerJobs, setOutsourcerJobs] = useState(
    jobData?.outsourcers || []
  );
  const [showAllOutsourcers, setShowAllOutsourcers] = useState(false);

  useEffect(() => {
    setOutsourcerJobs(jobData?.outsourcers || []);
  }, [jobData]);

  useEffect(() => {
    const fetchData = async () => {
      const tmp = [];
      const querySnapshot = await getDocs(collection(db, "Outsourcers"));
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (!showAllOutsourcers) {
          if (data.isPrimary) tmp.push(data);
        } else {
          tmp.push(data);
        }
      });
      setOutsourcerList(tmp);
    };
    fetchData();
  }, [showAllOutsourcers]);


  const handleNewJob = () => {
    const newJob = {
      outsourcer: "",
      dueDate: null,
      sentDate: null,
      staff: currentStaffData ? currentStaffData.firstName : "",
      lineItems: [{ type: "", images: "", price: "" }],
      includeRetouchingNotes: true,
      includeColourChanges: false,
    };
    const updatedJobs = [...outsourcerJobs, newJob];
    setOutsourcerJobs(updatedJobs);
    updateDoc(doc(db, "JobData", jobData.id), { outsourcers: updatedJobs }).catch((e) => console.error(e));
  };

  const handleDeleteJobClick = (jobIndex) => {
    setDeleteJobDialog({ open: true, jobIndex });
  };

  const handleDeleteJobConfirm = () => {
    const jobIndex = deleteJobDialog.jobIndex;
    if (jobIndex !== null) {
      const updatedJobs = outsourcerJobs.filter((_, index) => index !== jobIndex);
      setOutsourcerJobs(updatedJobs);
      updateDoc(doc(db, "JobData", jobData.id), { outsourcers: updatedJobs }).catch((e) => console.error(e));
    }
    setDeleteJobDialog({ open: false, jobIndex: null });
  };

  const handleDeleteJobCancel = () => {
    setDeleteJobDialog({ open: false, jobIndex: null });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 30,
        mass: 1,
      }}
    >
      {/* Outsourcer Briefs Section */}
      <OutsourcerBriefsContainer 
        jobData={jobData} 
        clientData={clientData} 
        setHasUnsavedChanges={setHasUnsavedChanges} 
      />

      <Box sx={{ p: 1, position: "relative" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            borderRadius: "10px",
            overflow: "visible",
            backgroundColor:
              theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow:
              theme === "dark"
                ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
            padding: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 2,
              position: "relative",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color:
                  theme === "dark"
                    ? "rgba(255,255,255,0.8)"
                    : "rgba(0,0,0,0.8)",
                fontWeight: "bold",
                fontSize: "1.1rem",
              }}
            >
              Outsourcers
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showAllOutsourcers}
                      onChange={(_, checked) => {
                        setShowAllOutsourcers(checked);
                      }}
                      size="small"
                    />
                  }
                  sx={{
                    cursor: "pointer",
                    MozUserSelect: "none",
                    WebkitUserSelect: "none",
                    msUserSelect: "none",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.875rem",
                    },
                  }}
                  label="Show All Outsourcers"
                />
              </FormGroup>

              <Box sx={{ display: "flex" }}>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <IconButton
                    onClick={handleNewJob}
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(45,45,45,0.7)"
                          : "rgba(245,245,245,0.9)",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(55,55,55,0.9)"
                            : "rgba(235,235,235,1)",
                      },
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </motion.div>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            {outsourcerJobs?.map((job, index) => (
              <OutsourcerJob
                key={`outsourcerJob-${index}`}
                jobData={jobData}
                outsourcerList={outsourcerList}
                job={job}
                index={index}
                onDeleteJob={() => handleDeleteJobClick(index)}
                setOutsourcerJobs={setOutsourcerJobs}
                clientData={clientData}
                setOutsourcerEmailOpen={setOutsourcerEmailOpen}
                setOutsourcerEmailData={setOutsourcerEmailData}
                theme={theme}
              />
            ))}

            {outsourcerJobs?.length === 0 && (
              <Box
                sx={{
                  textAlign: "center",
                  color:
                    theme === "dark"
                      ? "rgba(255,255,255,0.5)"
                      : "rgba(0,0,0,0.5)",
                  py: 4,
                  fontStyle: "italic",
                }}
              >
                No outsourcer jobs yet. Click the + button to add one.
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Delete Job Confirmation Dialog */}
      <Dialog
        open={deleteJobDialog.open}
        onClose={handleDeleteJobCancel}
        aria-labelledby="delete-job-dialog-title"
        aria-describedby="delete-job-dialog-description"
      >
        <DialogTitle id="delete-job-dialog-title">
          Delete Outsourcer Job
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-job-dialog-description">
            Are you sure you want to delete this outsourcer job? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteJobCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteJobConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* New clean OutsourcerEmailV2 component */}
      <OutsourcerEmailV2
        open={outsourcerEmailOpen}
        onClose={() => setOutsourcerEmailOpen(false)}
        jobData={outsourcerEmailData?.jobData}
        outsourcerJob={outsourcerEmailData?.outsourcerJob}
        outsourcerIndex={outsourcerEmailData?.outsourcerIndex}
        outsourcerData={outsourcerEmailData?.outsourcerData}
      />
    </motion.div>
  );
};

// Sub-components for better organization
const OutsourcerJob = ({
  jobData,
  outsourcerList,
  job,
  index,
  onDeleteJob,
  setOutsourcerJobs,
  clientData,
  setOutsourcerEmailOpen,
  setOutsourcerEmailData,
  theme,
}) => {
  const [outsourcer, setOutsourcer] = useState(job.outsourcer);
  const [dueDate, setDueDate] = useState(parseDate(job.dueDate));
  const [staff, setStaff] = useState(job.staff);
  const [lineItems, setLineItems] = useState(job.lineItems);
  const [total, setTotal] = useState("");
  const [includeRetouchingNotes, setIncludeRetouchingNotes] = useState(
    job.includeRetouchingNotes
  );
  const [includeColourChanges, setIncludeColourChanges] = useState(
    job.includeColourChanges
  );
  const [emailSent, setEmailSent] = useState(job.emailSent || false);
  const [alertDueDate, setAlertDueDate] = useState("");
  const [openAlert, setOpenAlert] = useState(false);

  useEffect(() => {
    setOutsourcer(job.outsourcer);
    setDueDate(parseDate(job.dueDate));
    setStaff(job.staff);
    setLineItems(job.lineItems);
    setEmailSent(job.emailSent);
    setIncludeColourChanges(job.includeColourChanges);
    updateTotal(job, setTotal);
  }, [job]);


  const handleOutsourcerChange = (value) => {
    job.outsourcer = value;
    setOutsourcer(value);
    updateDoc(doc(db, "JobData", jobData.id), { outsourcers: jobData.outsourcers }).catch((e) => console.error(e));
  };

  const handleDueDateChange = (value) => {
    const moreThanFour = isMoreThanFourDaysFuture(value);
    if (!moreThanFour) {
      job.dueDate = value.toISOString();
      setDueDate(value);
      updateDoc(doc(db, "JobData", jobData.id), { outsourcers: jobData.outsourcers }).catch((e) => console.error(e));
    } else {
      setAlertDueDate(value);
      setOpenAlert(true);
    }
  };

  const handleNewLine = () => {
    job.lineItems.push({ type: "", images: "", price: "" });
    setLineItems([...job.lineItems]);
    updateDoc(doc(db, "JobData", jobData.id), { outsourcers: jobData.outsourcers }).catch((e) => console.error(e));
  };

  const handleDeleteLine = (index) => {
    job.lineItems.splice(index, 1);
    setLineItems([...job.lineItems]);
    updateDoc(doc(db, "JobData", jobData.id), { outsourcers: jobData.outsourcers }).catch((e) => console.error(e));
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  function isMoreThanFourDaysFuture(date) {
    const currentDate = dayjs().startOf("day");
    const selectedDate = dayjs(date).startOf("day");
    const differenceInDays = selectedDate.diff(currentDate, "day");
    return differenceInDays > 4;
  }


  const sendEmail = async (event) => {
    // Check if email button is disabled and Option key is pressed
    if (emailSent && event?.altKey) {
      // Reset email sent status
      job.emailSent = false;
      setEmailSent(false);
      
      // Update in database
      const currentOutsourcers = jobData?.outsourcers || [];
      const newOutsourcers = [...currentOutsourcers];
      newOutsourcers[index] = job;
      updateDoc(doc(db, "JobData", jobData.id), { outsourcers: newOutsourcers })
        .catch((e) => console.error(e));
      
      return;
    }

    // If email already sent and Option key not pressed, don't open dialog
    if (emailSent && !event?.altKey) {
      return;
    }

    if (!dueDate || !staff || !outsourcer) {
      alert("Please fill in due date, staff, and outsourcer");
      return;
    }

    // Get outsourcer data from the list
    const outsourcerData = outsourcerList.find(o => o.companyName === outsourcer);
    if (!outsourcerData) {
      alert("Outsourcer data not found");
      return;
    }

    // Set up clean email data - no more string parsing!
    setOutsourcerEmailData({
      jobData,
      outsourcerJob: job,
      outsourcerIndex: index,
      outsourcerData,
    });
    
    // Open the new clean email modal
    setOutsourcerEmailOpen(true);
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: "8px",
        backgroundColor:
          theme === "dark" ? "rgba(45,45,45,0.5)" : "rgba(245,245,245,0.5)",
        padding: 2,
        marginBottom: 1,
      }}
    >

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            fontSize: "1.6rem",
            color:
              theme === "dark" ? "rgb(192, 194, 195)" : "rgba(0, 0, 0, 0.6)",
          }}
        >
          {numToLetter(index + 1)}
        </Typography>

        <Box sx={{ display: "flex", gap: 1 }}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeRetouchingNotes}
                  onChange={(_, checked) => {
                    job.includeRetouchingNotes = checked;
                    setIncludeRetouchingNotes(checked);
                    updateDoc(doc(db, "JobData", jobData.id), { outsourcers: jobData.outsourcers }).catch((e) => console.error(e));
                  }}
                  size="small"
                />
              }
              label="Retouching Notes"
              sx={{ fontSize: "0.875rem" }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeColourChanges}
                  onChange={(_, checked) => {
                    job.includeColourChanges = checked;
                    setIncludeColourChanges(checked);
                    updateDoc(doc(db, "JobData", jobData.id), { outsourcers: jobData.outsourcers }).catch((e) => console.error(e));
                  }}
                  size="small"
                />
              }
              label="Colour Changes"
              sx={{ fontSize: "0.875rem" }}
            />
          </FormGroup>

          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              onClick={onDeleteJob}
              size="small"
              sx={{ 
                width: 34, 
                height: 34,
                color: theme === "dark" ? "rgba(255,100,100,0.8)" : "rgba(200,50,50,0.8)",
                "&:hover": {
                  color: theme === "dark" ? "rgba(255,150,150,1)" : "rgba(255,100,100,1)",
                  backgroundColor: theme === "dark" ? "rgba(255,100,100,0.1)" : "rgba(255,200,200,0.3)"
                }
              }}
              title="Delete this outsourcer job"
            >
              <DeleteIcon />
            </IconButton>
            <IconButton
              onClick={handleNewLine}
              size="small"
              sx={{ width: 34, height: 34 }}
            >
              <AddIcon />
            </IconButton>
            <IconButton
              onClick={(event) => sendEmail(event)}
              size="small"
              sx={{ 
                width: 34, 
                height: 34,
                opacity: emailSent ? 0.5 : 1
              }}
              title={emailSent ? "Email sent (Option+click to reset)" : "Send email"}
            >
              <MailIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Outsourcer selection */}
      <FormControl
        fullWidth
        sx={{
          marginTop: 1,
          position: "relative",
          "& .MuiInputLabel-root": {
            color:
              theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
            fontWeight: 500,
            fontSize: "0.95rem",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&.MuiInputLabel-shrink": {
              transform: "translate(14px, 7px) scale(0.75)",
            },
          },
          "& .MuiInputBase-root": {
            borderRadius: "8px",
            backgroundColor:
              theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(250,250,250,0.9)",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              backgroundColor:
                theme === "dark" ? "rgba(55,55,55,0.8)" : "rgba(255,255,255,1)",
            },
            "&.Mui-focused": {
              backgroundColor:
                theme === "dark" ? "rgba(60,60,60,0.9)" : "rgba(255,255,255,1)",
              boxShadow:
                theme === "dark"
                  ? "0 0 0 2px rgba(100,100,255,0.5)"
                  : "0 0 0 2px rgba(100,100,255,0.3)",
            },
          },
        }}
        variant="filled"
      >
        <Autocomplete
          value={outsourcer}
          disableClearable
          onChange={(_, newValue) => {
            handleOutsourcerChange(newValue);
          }}
          options={outsourcerList?.map((o) => o.companyName)}
          getOptionLabel={(opts) => opts}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Outsourcer"
              label="Outsourcer"
              variant="filled"
              slotProps={{
                input: { ...params.InputProps, disableUnderline: true },
              }}
            />
          )}
        />
      </FormControl>

      {/* Briefs Selection */}
      <BriefsSelector
        jobData={jobData}
        clientData={clientData}
        job={job}
        theme={theme}
      />

      {/* Staff, Due Date, Total */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-evenly",
          gap: 1,
          marginTop: 1,
        }}
      >
        <UniversalTextField
          label="Staff"
          value={staff || ""}
          variant="filled"
          noPadding={true}
          onCallback={(value) => {
            job.staff = value;
            setStaff(value);
            // Update the entire outsourcers array
            const currentOutsourcers = jobData?.outsourcers || [];
            const newOutsourcers = [...currentOutsourcers];
            newOutsourcers[index] = job;
            updateDoc(doc(db, "JobData", jobData.id), { outsourcers: newOutsourcers })
              .catch((e) => console.error(e));
          }}
          adminOnly={false}
          sx={{
            marginTop: 0,
            "& .MuiFormControl-root": {
              marginTop: 0,
            },
            "& .MuiFormControl-root > .MuiBox-root": {
              backgroundColor: "transparent !important",
              boxShadow: "none !important",
              borderRadius: "8px",
              padding: 1,
            },
            "& .MuiInputBase-root": {
              borderRadius: "8px",
              backgroundColor:
                theme === "dark"
                  ? "rgba(45,45,45,0.7)"
                  : "rgba(250,250,250,0.9)",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                backgroundColor:
                  theme === "dark"
                    ? "rgba(55,55,55,0.8)"
                    : "rgba(255,255,255,1)",
              },
              "&.Mui-focused": {
                backgroundColor:
                  theme === "dark"
                    ? "rgba(60,60,60,0.9)"
                    : "rgba(255,255,255,1)",
                boxShadow:
                  theme === "dark"
                    ? "0 0 0 2px rgba(100,100,255,0.5)"
                    : "0 0 0 2px rgba(100,100,255,0.3)",
              },
            },
            "& .MuiInputLabel-root": {
              color:
                theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
              fontWeight: 500,
              fontSize: "0.95rem",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&.MuiInputLabel-shrink": {
                transform: "translate(14px, 7px) scale(0.75)",
              },
            },
          }}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <MobileDatePicker
            label="Due"
            fullWidth
            value={dueDate || null}
            onChange={(d) => {
              handleDueDateChange(d);
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "filled",
                InputProps: { disableUnderline: true },
                sx: {
                  "& .MuiInputLabel-root": {
                    color:
                      theme === "dark"
                        ? "rgba(255,255,255,0.7)"
                        : "rgba(0,0,0,0.7)",
                    fontWeight: 500,
                    fontSize: "0.95rem",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&.MuiInputLabel-shrink": {
                      transform: "translate(14px, 7px) scale(0.75)",
                    },
                  },
                  "& .MuiFilledInput-root": {
                    borderRadius: "8px",
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(45,45,45,0.7)"
                        : "rgba(250,250,250,0.9)",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(55,55,55,0.8)"
                          : "rgba(255,255,255,1)",
                    },
                    "&.Mui-focused": {
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(60,60,60,0.9)"
                          : "rgba(255,255,255,1)",
                      boxShadow:
                        theme === "dark"
                          ? "0 0 0 2px rgba(100,100,255,0.5)"
                          : "0 0 0 2px rgba(100,100,255,0.3)",
                    },
                  },
                },
              },
            }}
          />
        </LocalizationProvider>
        <UniversalTextField
          label="Total"
          value={total || ""}
          variant="filled"
          disabled={true}
          noPadding={true}
          adminOnly={false}
          sx={{
            marginTop: 0,
            "& .MuiFormControl-root": {
              marginTop: 0,
            },
            "& .MuiFormControl-root > .MuiBox-root": {
              backgroundColor: "transparent !important",
              boxShadow: "none !important",
              borderRadius: "8px",
              padding: 1,
            },
            "& .MuiInputBase-root": {
              borderRadius: "8px",
              backgroundColor:
                theme === "dark"
                  ? "rgba(45,45,45,0.7)"
                  : "rgba(250,250,250,0.9)",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                backgroundColor:
                  theme === "dark"
                    ? "rgba(55,55,55,0.8)"
                    : "rgba(255,255,255,1)",
              },
              "&.Mui-focused": {
                backgroundColor:
                  theme === "dark"
                    ? "rgba(60,60,60,0.9)"
                    : "rgba(255,255,255,1)",
                boxShadow:
                  theme === "dark"
                    ? "0 0 0 2px rgba(100,100,255,0.5)"
                    : "0 0 0 2px rgba(100,100,255,0.3)",
              },
            },
            "& .MuiInputLabel-root": {
              color:
                theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
              fontWeight: 500,
              fontSize: "0.95rem",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&.MuiInputLabel-shrink": {
                transform: "translate(14px, 7px) scale(0.75)",
              },
            },
          }}
        />
      </Box>

      {/* Line Items */}
      {lineItems?.map((item, i) => (
        <Box
          key={`outsourcerJob-${index}-lineItem-${i}`}
          sx={{
            display: "flex",
            marginTop: 1,
            alignItems: "center",
            gap: 1,
          }}
        >
          <OutsourcerLineItemV2
            jobData={jobData}
            job={job}
            item={item}
            index={i}
            jobIndex={index}
            setTotal={setTotal}
            onDeleteLine={() => handleDeleteLine(i)}
            theme={theme}
          />
        </Box>
      ))}

      {/* Alert Dialog for Due Date */}
      <AlertDialogV2
        open={openAlert}
        onClose={handleCloseAlert}
        alertDueDate={alertDueDate}
        setDueDate={setDueDate}
        jobData={jobData}
        job={job}
      />
    </Box>
  );
};

// Alert Dialog Component
const AlertDialogV2 = ({
  open,
  onClose,
  alertDueDate,
  setDueDate,
  jobData,
  job,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        {"Is the due date correct?"}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          The due date selected (
          {formatISODateToDayMonthWithDaysFromNow(alertDueDate)}) is pretty far
          in the future, is this correct?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button color="error" onClick={onClose}>
          Oops I'll change that!
        </Button>
        <Button
          onClick={() => {
            job.dueDate = alertDueDate.toISOString();
            setDueDate(alertDueDate);
            updateDoc(doc(db, "JobData", jobData.id), { outsourcers: jobData.outsourcers }).catch((e) => console.error(e));
            onClose();
          }}
          autoFocus
        >
          Yep that's correct!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Outsourcer Line Item Component
const OutsourcerLineItemV2 = ({
  jobData,
  job,
  item,
  index,
  jobIndex,
  setTotal,
  onDeleteLine,
  theme,
}) => {
  const [type, setType] = useState(item.type);
  const [images, setImages] = useState(item.images);
  const [price, setPrice] = useState(item.price);

  const typeArray2 = [
    {
      name: "Complete Retouch - Ghost & Deep Etch",
      price: "2.00",
    },
    {
      name: "Level 1",
      price: "1.50",
    },
    {
      name: "Level 2",
      price: "2.00",
    },
    {
      name: "Level 3",
      price: "2.50",
    },
    {
      name: "Level 4",
      price: "3.00",
    },
    {
      name: "Content",
      price: "3.00",
    },
  ];

  const priceArray = [
    "0.80",
    "1.00",
    "1.20",
    "1.40",
    "1.50",
    "2.00",
    "2.50",
    "3.00",
    "3.50",
    "4.00",
  ];

  useEffect(() => {
    setType(item.type);
    setImages(item.images);
    setPrice(item.price);
    updateTotal(job, setTotal);
  }, [item, job, setTotal]);

  const handlePriceChange = (value) => {
    job.lineItems[index].price = value;
    setPrice(value);
    updateDoc(doc(db, "JobData", jobData.id), { outsourcers: jobData.outsourcers }).catch((e) => console.error(e));
    updateTotal(job, setTotal);
  };

  const handleTypeChange = (value) => {
    job.lineItems[index].type = value;
    setType(value);
    updateDoc(doc(db, "JobData", jobData.id), { outsourcers: jobData.outsourcers }).catch((e) => console.error(e));
  };


  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
      }}
    >
      <Typography sx={{ marginLeft: 1, marginRight: 1, minWidth: "20px" }}>
        {index + 1}.
      </Typography>
      <FormControl
        fullWidth
        sx={{
          width: 365,
          "& .MuiInputLabel-root": {
            color:
              theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
            fontWeight: 500,
            fontSize: "0.85rem",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&.MuiInputLabel-shrink": {
              transform: "translate(14px, 7px) scale(0.75)",
            },
          },
          "& .MuiInputBase-root": {
            borderRadius: "8px",
            backgroundColor:
              theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(250,250,250,0.9)",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              backgroundColor:
                theme === "dark" ? "rgba(55,55,55,0.8)" : "rgba(255,255,255,1)",
            },
            "&.Mui-focused": {
              backgroundColor:
                theme === "dark" ? "rgba(60,60,60,0.9)" : "rgba(255,255,255,1)",
              boxShadow:
                theme === "dark"
                  ? "0 0 0 2px rgba(100,100,255,0.5)"
                  : "0 0 0 2px rgba(100,100,255,0.3)",
            },
          },
        }}
        variant="filled"
        size="small"
      >
        <Autocomplete
          value={type}
          onChange={(_, newValue) => {
            // Only handle dropdown selections, not manual typing
            if (typeof newValue === "object" && newValue !== null) {
              handleTypeChange(newValue.name);
              if (newValue.price) {
                handlePriceChange(newValue.price);
              }
            }
          }}
          options={typeArray2}
          freeSolo
          getOptionLabel={(option) => {
            return typeof option === "string"
              ? option
              : `${option.name} - $${option.price}`;
          }}
          disableClearable
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select Type"
              label="Image Type"
              size="small"
              variant="filled"
              onBlur={(e) => {
                // Handle manual typing on blur, like UniversalTextField
                const inputValue = e.target.value;
                if (inputValue !== type) {
                  handleTypeChange(inputValue);
                }
              }}
              slotProps={{
                input: { ...params.InputProps, disableUnderline: true },
              }}
            />
          )}
          isOptionEqualToValue={(option, value) => option.name === value}
        />
      </FormControl>
      <UniversalTextField
        label="Images"
        value={images || ""}
        variant="filled"
        noPadding={true}
        onCallback={(value) => {
          job.lineItems[index].images = value;
          setImages(value);
          updateTotal(job, setTotal);
          // Update the entire outsourcers array
          const currentOutsourcers = jobData?.outsourcers || [];
          const newOutsourcers = [...currentOutsourcers];
          newOutsourcers[jobIndex] = job;
          updateDoc(doc(db, "JobData", jobData.id), { outsourcers: newOutsourcers })
            .catch((e) => console.error(e));
        }}
        adminOnly={false}
        sx={{
          maxWidth: 100,
          marginTop: 0,
          height: 50,
          boxShadow: "none !important",
          "& .MuiFormControl-root > .MuiBox-root": {
            backgroundColor: "transparent !important",
            boxShadow: "none !important",
            borderRadius: "8px",
            padding: 1,
          },
          "& .MuiInputBase-root": {
            borderRadius: "8px",
            backgroundColor:
              theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(250,250,250,0.9)",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              backgroundColor:
                theme === "dark"
                  ? "rgba(55,55,55,0.8)"
                  : "rgba(255,255,255,1)",
            },
            "&.Mui-focused": {
              backgroundColor:
                theme === "dark"
                  ? "rgba(60,60,60,0.9)"
                  : "rgba(255,255,255,1)",
              boxShadow:
                theme === "dark"
                  ? "0 0 0 2px rgba(100,100,255,0.5)"
                  : "0 0 0 2px rgba(100,100,255,0.3)",
            },
          },
          "& .MuiInputLabel-root": {
            color:
              theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
            fontWeight: 500,
            fontSize: "0.85rem",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&.MuiInputLabel-shrink": {
              transform: "translate(14px, 7px) scale(0.75)",
            },
          },
        }}
      />
      <FormControl
        fullWidth
        sx={{
          maxWidth: 100,
          "& .MuiInputLabel-root": {
            color:
              theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
            fontWeight: 500,
            fontSize: "0.85rem",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&.MuiInputLabel-shrink": {
              transform: "translate(14px, 7px) scale(0.75)",
            },
          },
          "& .MuiInputBase-root": {
            borderRadius: "8px",
            backgroundColor:
              theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(250,250,250,0.9)",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              backgroundColor:
                theme === "dark" ? "rgba(55,55,55,0.8)" : "rgba(255,255,255,1)",
            },
            "&.Mui-focused": {
              backgroundColor:
                theme === "dark" ? "rgba(60,60,60,0.9)" : "rgba(255,255,255,1)",
              boxShadow:
                theme === "dark"
                  ? "0 0 0 2px rgba(100,100,255,0.5)"
                  : "0 0 0 2px rgba(100,100,255,0.3)",
            },
          },
        }}
        variant="filled"
        size="small"
      >
        <Autocomplete
          disableClearable
          value={price}
          onChange={(_, newValue) => {
            // Only handle dropdown selections
            if (typeof newValue === "string" && priceArray.includes(newValue)) {
              handlePriceChange(newValue);
            }
          }}
          options={priceArray}
          freeSolo
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              label="Price"
              size="small"
              variant="filled"
              onBlur={(e) => {
                // Handle manual typing on blur, like UniversalTextField
                const inputValue = e.target.value;
                if (inputValue !== price) {
                  handlePriceChange(inputValue);
                }
              }}
              slotProps={{
                input: { ...params.InputProps, disableUnderline: true },
              }}
            />
          )}
        />
      </FormControl>
      <IconButton
        onClick={onDeleteLine}
        size="small"
        sx={{
          width: 34,
          height: 34,
          color: theme === "dark" ? "rgba(255,100,100,0.7)" : "rgba(200,50,50,0.7)",
          "&:hover": {
            color: theme === "dark" ? "rgba(255,150,150,1)" : "rgba(255,100,100,1)",
            backgroundColor: theme === "dark" ? "rgba(255,100,100,0.1)" : "rgba(255,200,200,0.3)"
          }
        }}
        title="Delete this line item"
      >
        <RemoveIcon />
      </IconButton>
    </Box>
  );
};

// Helper functions
function updateTotal(job, setTotal) {
  let total = 0;
  job.lineItems?.forEach((item) => {
    if (item.price && item.images) {
      total += item.price * item.images;
    }
  });
  setTotal(`$${total.toFixed(2)}`);
}

function numToLetter(num) {
  let s = "",
    t;
  while (num > 0) {
    t = (num - 1) % 26;
    s = String.fromCharCode(65 + t) + s;
    num = ((num - t) / 26) | 0;
  }
  return s || undefined;
}

function formatISODateToDayMonthWithDaysFromNow(date) {
  const inputDate = dayjs(date).startOf("day");
  const currentDate = dayjs().startOf("day");

  const day = inputDate.date();
  const month = inputDate.month() + 1;

  const differenceInDays = inputDate.diff(currentDate, "day");

  return `${day}/${month} - ${differenceInDays} Days`;
}

const parseDate = (date) => {
  if (!date) return null;
  return dayjs(date);
};

const BriefsSelector = ({ jobData, clientData, job, theme }) => {
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const allBriefs = [
    ...(clientData?.outsourcerBriefs || []),
    ...(jobData?.outsourcerBriefs || []),
  ];

  const [selectedBriefs, setSelectedBriefs] = useState(job.briefs || []);

  useEffect(() => {
    if (!job.briefs) {
      const defaultBriefs = [
        ...(jobData?.outsourcerBriefs || []),
        ...(clientData?.outsourcerBriefs || []).filter((b) => b.default),
      ];
      job.briefs = defaultBriefs;
      setSelectedBriefs(defaultBriefs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBriefsChange = (event, newValue) => {
    job.briefs = newValue;
    setSelectedBriefs(newValue);
    updateDoc(doc(db, "JobData", jobData.id), {
      outsourcers: jobData.outsourcers,
    }).catch((e) => console.error(e));
  };

  return (
    <FormControl
      fullWidth
      sx={{
        marginTop: 1,
        position: "relative",
        "& .MuiInputLabel-root": {
          color:
            theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
          fontWeight: 500,
          fontSize: "0.95rem",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, 7px) scale(0.75)",
          },
        },
        "& .MuiInputBase-root": {
          borderRadius: "8px",
          backgroundColor:
            theme === "dark" ? "rgba(45,45,45,0.7)" : "rgba(250,250,250,0.9)",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            backgroundColor:
              theme === "dark" ? "rgba(55,55,55,0.8)" : "rgba(255,255,255,1)",
          },
          "&.Mui-focused": {
            backgroundColor:
              theme === "dark" ? "rgba(60,60,60,0.9)" : "rgba(255,255,255,1)",
            boxShadow:
              theme === "dark"
                ? "0 0 0 2px rgba(100,100,255,0.5)"
                : "0 0 0 2px rgba(100,100,255,0.3)",
          },
        },
      }}
      variant="filled"
    >
      <Autocomplete
        multiple
        options={allBriefs}
        value={selectedBriefs}
        disableClearable
        disableCloseOnSelect
        getOptionLabel={(option) => option.name || ""}
        isOptionEqualToValue={(option, value) =>
          option.name === value.name && option.url === value.url
        }
        onChange={handleBriefsChange}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option.name}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="filled"
            label="Briefs"
            placeholder="Select briefs"
            InputProps={{ ...params.InputProps, disableUnderline: true }}
          />
        )}
      />
    </FormControl>
  );
};

export default OutsourcersTab;